import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { PageHeader } from "~/components/page-header";
import { DataTable } from "~/components/data-table";
import { MasterDetail } from "~/components/master-detail";
import { SearchInput } from "~/components/search-input";
import { DetailField } from "~/components/detail-field";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { api } from "~/lib/api";
import { formatKES, formatDate, type BadgeVariant } from "~/lib/format";
import type { ITransaction, IPaymentMetrics } from "~/lib/proto";

export const Route = createFileRoute("/_authed/ops/transactions")({
  component: Transactions,
});

const STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "SUCCESS", label: "Success" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const RANGES = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

function statusVariant(status?: string | null): BadgeVariant {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
      return "destructive";
    default:
      return "secondary";
  }
}

function rangeFrom(range: string): string | undefined {
  const now = new Date();
  if (range === "today") {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return d.toISOString();
  }
  if (range === "7d") return new Date(now.getTime() - 7 * 864e5).toISOString();
  if (range === "30d") return new Date(now.getTime() - 30 * 864e5).toISOString();
  return undefined;
}

function Transactions() {
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("all");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<ITransaction | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data: metrics } = useQuery({
    queryKey: ["ops", "transactions", "metrics"],
    queryFn: () => api.ops.paymentMetrics(),
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["ops", "transactions", status, range, q],
    queryFn: () =>
      api.ops.listTransactions({
        status: status === "all" ? undefined : status,
        from: rangeFrom(range),
        q: q || undefined,
      }),
  });

  const columns = useMemo<ColumnDef<ITransaction, unknown>[]>(
    () => [
      {
        header: "Customer",
        accessorKey: "customerName",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium">{row.original.customerName || "Unknown"}</p>
            <p className="text-text-faint font-mono text-[11px]">{row.original.customerPhone || "—"}</p>
          </div>
        ),
      },
      {
        header: "Parcel ref",
        accessorKey: "labelCode",
        cell: ({ row }) => {
          const count = Number(row.original.parcelCount ?? 0);
          if (count > 1) return <span className="text-text-faint text-[12px]">{count} parcels</span>;
          return <span className="font-mono text-[12px]">{row.original.labelCode || "—"}</span>;
        },
        meta: { headClassName: "w-[110px]" },
      },
      {
        header: "Amount",
        accessorKey: "amountKes",
        cell: ({ row }) => <span className="tabular font-medium">{formatKES(row.original.amountKes)}</span>,
        meta: { headClassName: "w-[120px]" },
      },
      {
        header: "Method",
        accessorKey: "source",
        cell: ({ row }) =>
          row.original.source === "MANUAL" ? (
            <Badge variant="outline">Manual</Badge>
          ) : (
            <span className="font-mono text-[12px]">{row.original.mpesaReceipt || "M-Pesa"}</span>
          ),
        meta: { headClassName: "w-[120px]" },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>,
        meta: { headClassName: "w-[100px]" },
      },
      {
        header: "Date",
        accessorKey: "createdAt",
        cell: ({ row }) => <span className="text-text-faint text-[12px]">{formatDate(row.original.createdAt)}</span>,
        meta: { headClassName: "w-[160px]" },
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader eyebrow="Operations" title="Transactions" caption="Every M-Pesa and manual payment across ParcelPoint." />

      <MetricCards metrics={metrics} />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          className="min-w-[200px] flex-1"
          placeholder="Search name, phone, receipt or code…"
          value={search}
          onChange={setSearch}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <MasterDetail
        open={!!selected}
        onClose={() => setSelected(null)}
        detail={selected ? <TransactionDetail tx={selected} /> : null}
        emptyHint="Select a transaction to see its details"
        title="Transaction"
      >
        <DataTable
          data={rows}
          columns={columns}
          limit={200}
          offset={0}
          onPageChange={() => {}}
          loading={isLoading}
          getRowId={(t) => t.id!}
          onRowClick={(t) => setSelected(t)}
          selectedRowId={selected?.id ?? undefined}
          emptyTitle="No transactions"
          emptyBody="Payments will appear here as customers pay."
        />
      </MasterDetail>
    </>
  );
}

function TransactionDetail({ tx }: { tx: ITransaction }) {
  const manual = tx.source === "MANUAL";
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 p-4 pr-10">
        <div>
          <p className="eyebrow">Amount</p>
          <p className="mt-0.5 text-[26px] font-semibold tracking-[-0.02em] tabular">{formatKES(tx.amountKes)}</p>
          <div className="mt-2 flex items-center gap-1.5">
            <Badge variant={statusVariant(tx.status)}>{tx.status}</Badge>
            <Badge variant="outline">{manual ? "Manual" : "M-Pesa"}</Badge>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-3">
          <p className="eyebrow mb-1">Customer</p>
          <p className="text-[14px] font-medium">{tx.customerName || "Unknown"}</p>
          <p className="text-text-faint font-mono text-[12px]">{tx.customerPhone || "—"}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DetailField label={manual ? "Recorded by" : "M-Pesa receipt"}>
            <span className="font-mono text-[12px]">{manual ? (tx.paidBy || "—") : (tx.mpesaReceipt || "—")}</span>
          </DetailField>
          <DetailField label="Account ref"><span className="font-mono text-[12px]">{tx.accountReference || "—"}</span></DetailField>
          <DetailField label="Parcel">
            {Number(tx.parcelCount ?? 0) > 1 ? `${Number(tx.parcelCount)} parcels` : <span className="font-mono text-[12px]">{tx.labelCode || "—"}</span>}
          </DetailField>
          <DetailField label="Date">{formatDate(tx.createdAt) || "—"}</DetailField>
        </div>

        {tx.status === "FAILED" && tx.resultDesc && (
          <>
            <Separator />
            <div>
              <p className="eyebrow mb-1">Failure reason</p>
              <p className="text-destructive text-[13px]">{tx.resultDesc}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MetricCards({ metrics }: { metrics?: IPaymentMetrics }) {
  const cards = [
    { label: "Collected", value: formatKES(metrics?.totalCollectedKes), sub: `${Number(metrics?.successCount ?? 0)} paid` },
    { label: "Pending", value: formatKES(metrics?.totalPendingKes), sub: `${Number(metrics?.pendingCount ?? 0)} awaiting` },
    { label: "Failed", value: formatKES(metrics?.totalFailedKes), sub: `${Number(metrics?.failedCount ?? 0)} failed` },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardContent className="pt-0">
              <p className="eyebrow">{c.label}</p>
              <p className="mt-1 text-[24px] font-semibold tracking-[-0.02em] tabular">{c.value}</p>
              <p className="text-text-faint mt-0.5 text-[12px]">{c.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      {metrics?.periods && metrics.periods.length > 0 && (
        <div className="border-hairline grid grid-cols-3 divide-x rounded-lg border">
          {metrics.periods.map((p) => (
            <div key={p.label} className="px-3 py-2.5">
              <p className="text-text-faint text-[11px]">{p.label}</p>
              <p className="tabular mt-0.5 text-[15px] font-semibold">{formatKES(p.collectedKes)}</p>
              <p className="text-text-faint text-[11px]">{Number(p.count ?? 0)} paid</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
